import { cloneDeep, findIndex, isEqual, isNumber, pullAll, toInteger } from 'lodash';
import {
    Framework,
    getFramework,
    getTrackFunc,
    isLayerProps,
    resetGroupLayerRotate,
    resetLayer,
    type IFramework,
} from '@/framework';
import { createAgentInfo } from '@/utils/agent-teams-kit';
import { getRotatePosition } from '@/utils/collision-kit';
import { getLastMousePoint } from '@/utils/event-kit';
import { getLayerInfoWithRotate, isRectOutOfBounds } from '@/utils/layer-kit';
import { asyncTraverseTrees, traverseTree, traverseTrees } from '@/utils/tree-kit';
import {
    COPY_DISTANCE,
    LayerType,
    type IAgentInfo,
    type IExtraInfo,
    type IImageLayerProps,
    type IImageTransform,
    type ILayerProps,
    type IPoint,
} from '@/version-types';
import { CanvasAlign } from './canvas-align';
import { GroupAlign } from './group-align';
import { ModifyBasicProperties } from './modify-basic-properties';
import { HistoryType } from './type';

export * from './plugins';

/** 添加图层参数 */
export interface IAddLayerArgs {
    /** 是否检查聚焦，默认 false */
    checkFocus?: boolean;
    /** 图层 */
    layer: ILayerProps;
    /** 是否激活新图层，默认true */
    isActiveNewLayer?: boolean;
    /** 是否写入历史纪录，默认true */
    isWriteHistory?: boolean;
    /** 统计值 */
    trackFunc?: string;
    /** 目标原始索引 */
    destOriginIndex?: number;
    /** 画布对应的额外信息 */
    extraInfo?: {
        oldExtraInfo: IExtraInfo;
        newExtraInfo: IExtraInfo;
    };
}

/** 新图层的显式插入位置 */
export type LayerInsertPosition =
    | {
          type: 'stage-point';
          point: IPoint;
      }
    | {
          type: 'diagonal-from-layer';
          layer: ILayerProps;
          gap?: number;
      };

/** Agent 默认配置，子类可通过覆盖基类方法返回差异项。 */
interface IAgentCreateOptions {
    /** 是否禁用旋转操作 */
    disableRotate?: boolean;
    /** 是否禁用缩放操作 */
    disableScale?: boolean;
    /** 是否禁用调整大小操作 */
    disableResize?: boolean;
    /** 操作框边框圆角 */
    borderRadius?: number;
}

/** 创建 Agent 信息时的通用入参。 */
interface IGetAgentOptions {
    /** 所属任务 ID */
    taskId: string;
    /** 代理人 ID，作为布局分组标记 */
    id: string;
    /** 代理人名称 */
    name: string;
    /** 代理人头像 */
    avatar: string;
    /** 下载文件名 */
    downloadFileName?: string;
    /** 图层关联标记 */
    relationID?: string;
}

export class Layer extends Framework {
    /** 上一次添加的图层id */
    private preAddLayerId = '';

    /** 组内对齐 */
    groupAlign: GroupAlign;
    /** 画布对齐 */
    canvasAlign: CanvasAlign;
    /** 修改基础属性 */
    modifyBasicProperties: ModifyBasicProperties;

    constructor(identity: string) {
        super(identity);
        this.framework = getFramework(identity);
        this.groupAlign = new GroupAlign(this.framework);
        this.canvasAlign = new CanvasAlign(this.framework);
        this.modifyBasicProperties = new ModifyBasicProperties(this.framework, this.identity);
    }

    /** 获取代理人信息 */
    getAgent(options: IGetAgentOptions): IAgentInfo {
        const createOptions = this.getAgentCreateOptions();
        const canvasScale = this.framework.service.agentLayout.getCanvasScale() ?? 1;
        const borderRadius =
            createOptions.borderRadius != null ? createOptions.borderRadius * canvasScale : createOptions.borderRadius;
        return createAgentInfo({
            ...options,
            ...createOptions,
            borderRadius,
        });
    }

    /** 获取代理人默认配置，子类可按需覆盖。 */
    protected getAgentCreateOptions(): IAgentCreateOptions {
        return {
            // 禁止选择
            disableRotate: true,
            // 禁止缩放
            disableScale: false,
            // 禁止调整大小
            disableResize: false,
            // 操作框边框圆角
            borderRadius: 0,
        };
    }

    /** 强制更新图层 */
    forceUpdateLayer(layerId: string) {
        const layer = this.framework.service.flatLayer.getLayer(layerId, true);
        if (!layer) return;
        const newLayer = cloneDeep(layer);
        newLayer.__forceRenderKey = Date.now();
        this.framework.service.frame.addOrUpdateLayer(newLayer);
    }

    /**
     * 设置图层透明度
     *
     * @param layerId - 图层 ID
     * @param opacity - 透明度
     * @param originOpacity - 原始透明度，传入这个参数时，需要写入历史纪录
     * @returns
     */
    setOpacity(layerId: string, opacity: number, originOpacity?: number | null) {
        const currentLayer = this.framework.service.flatLayer.getLayer(layerId);
        const newLayer = cloneDeep(currentLayer);
        newLayer.opacity = opacity;

        // 透明度由 Edgeless Canvas 在合成阶段通过 globalAlpha 应用，并不会改变图层的可绘制内容。
        // 因此这里只更新基础信息，并合并请求下一帧重绘，避免滑块连续变化时反复加载资源和生成图片。
        this.framework.service.frame.addOrUpdateLayer(newLayer, undefined, true);
        this.framework.service.edgelessCanvas.requestRenderAll();

        if (isNumber(originOpacity)) {
            const oldLayer = cloneDeep(currentLayer);
            oldLayer.opacity = originOpacity;
            this.framework.service.history.write(oldLayer, getTrackFunc(this.identity, oldLayer, 'opacity'));
            this.framework.service.frame.setActiveLayer(newLayer.id);
        }
    }

    /**
     * 锁定图层
     *
     * @param layerId - 图层 ID
     */
    lock(layerId: string) {
        const oldLayer = this.framework.service.flatLayer.getLayer(layerId, true);
        if (!oldLayer) return;
        const trackFunc = getTrackFunc(this.identity, oldLayer, 'lock');
        const isExtraGroup = this.framework.service.flatLayer.checkIsExtraGroup(oldLayer);
        if (isExtraGroup) {
            const childIds = oldLayer.children?.map((item) => item.id);
            const res = this.framework.service.frame.tryRemoveGroup(oldLayer);
            if (!res) return;
            for (let i = 0; i < childIds.length; i++) {
                const childId = childIds[i];
                const childLayer = this.framework.service.flatLayer.getLayer(childId, true);
                if (!childLayer) continue;
                childLayer.lock = true;
                this.framework.service.frame.addOrUpdateLayer(childLayer);
            }
            this.framework.service.history.write({
                type: HistoryType.LOCK,
                data: {
                    item: cloneDeep(oldLayer),
                },
                originIndex: oldLayer.__originIndex,
                trackFunc,
            });
            this.framework.service.frame.delActiveLayer();
        } else {
            const newLayer = cloneDeep(oldLayer);
            newLayer.lock = true;
            this.framework.service.frame.addOrUpdateLayer(newLayer);
            this.framework.service.history.write(oldLayer, trackFunc);
            this.framework.service.frame.setActiveLayer(newLayer.id);
        }
    }

    /**
     * 解锁图层
     *
     * @param layerId - 图层 ID
     */
    unlock(layerId: string) {
        const oldLayer = this.framework.service.flatLayer.getLayer(layerId, true);
        if (!oldLayer) return;
        const newLayer = cloneDeep(oldLayer);
        newLayer.lock = false;
        this.framework.service.frame.addOrUpdateLayer(newLayer);
        this.framework.service.history.write(oldLayer, getTrackFunc(this.identity, oldLayer, 'lock'));
        this.framework.service.frame.setActiveLayer(newLayer.id);
    }
    /**
     * 排序图层
     * @param operateCount - 操作次数
     * @param data - 排序数据
     * @param pushHistory - 是否写入历史纪录
     */
    sort(
        operateCount: number,
        data: {
            /** 原始索引 */
            frameId: number;
            /** 排序列表 */
            sortList: { id: string; index: number }[];
            /** 子列表 */
            childList: { id: string; index: number }[];
        },
        pushHistory = true,
    ) {
        const sortList = cloneDeep(data.sortList);
        const childList = cloneDeep(data.childList);
        const oldSortList: { id: string; index: number }[] = [];
        if (pushHistory) {
            oldSortList.push(...[...cloneDeep(sortList), ...cloneDeep(childList)]);
            oldSortList.sort((a, b) => a.index - b.index);
            // console.log('sort oldSortList', oldSortList);
        }
        // 根据 sortList childList 里面的id 对应的 index 恢复 renderIds 的顺序
        const renderIds = this.framework.service.frame.getLayerIds(data.frameId);
        const originRenderIds = Array(renderIds.length).fill(undefined);
        const ids: string[] = [];
        for (let i = 0; i < sortList.length; i++) {
            const item = sortList[i];
            originRenderIds[item.index] = item.id;
            ids.push(item.id);
        }
        for (let i = 0; i < childList.length; i++) {
            const item = childList[i];
            originRenderIds[item.index] = item.id;
            ids.push(item.id);
        }
        const oldRenderIds = [...renderIds];
        pullAll(oldRenderIds, ids);
        for (let i = 0; i < renderIds.length; i++) {
            if (originRenderIds[i] === undefined) {
                const id = oldRenderIds.shift();
                if (id) {
                    originRenderIds[i] = id;
                }
            }
        }
        renderIds.length = 0;
        renderIds.push(...originRenderIds);

        // console.log('old', [...renderIds]);

        if (operateCount !== 0) {
            const newRenderIds = Array(renderIds.length).fill(undefined);
            let diffIndex = operateCount;

            let baseLastItem = childList[childList.length - 1];
            let baseFirstItem = childList[0];

            let index = 0;
            if (operateCount > 0) {
                for (let i = 0; i < childList.length; i++) {
                    const item = childList[i];
                    if (i === 0) {
                        index = item.index;
                    } else {
                        if (index + 1 !== item.index) break;
                        index = item.index;
                    }
                    baseFirstItem = item;
                }
            } else {
                for (let i = childList.length - 1; i >= 0; i--) {
                    const item = childList[i];
                    if (i === childList.length - 1) {
                        index = item.index;
                    } else {
                        if (index - 1 !== item.index) break;
                        index = item.index;
                    }
                    baseLastItem = item;
                }
            }

            if (operateCount < 0) {
                const target = baseLastItem; // childList[childList.length - 1];
                const source: { id: string; index: number }[] = [...sortList, target];
                source.sort((a, b) => a.index - b.index);
                const targetIndex = findIndex(source, (item) => item.id === target.id);
                diffIndex = source[targetIndex + operateCount].index - source[targetIndex].index;
                // console.log(
                //     'firstIndex 0 => ',
                //     source[targetIndex + operateCount].index,
                //     source[targetIndex].index,
                // );
            } else {
                const target = baseFirstItem; // childList[0];
                const source: { id: string; index: number }[] = [...sortList, target];
                source.sort((a, b) => a.index - b.index);
                const targetIndex = findIndex(source, (item) => item.id === target.id);
                diffIndex = source[targetIndex + operateCount].index - source[targetIndex].index;
            }
            // console.log('firstIndex 1 => ', { diffIndex });

            const childFirstIndex = baseFirstItem.index; // childList[0].index;
            const childLastIndex = baseLastItem.index; // childList[childList.length - 1].index;

            const ids: string[] = [];
            for (let i = 0; i < childList.length; i++) {
                const item = childList[i];
                item.index += diffIndex;
                ids.push(item.id);
            }
            // console.log('2 => ', { diffIndex, childList: cloneDeep(childList) });

            if (operateCount < 0) {
                let firstIndex = Math.min(sortList[0].index, childFirstIndex);
                // console.log('firstIndex 2 =>', firstIndex, { s: sortList[0].index, childFirstIndex, childLastIndex });

                for (let i = 0; i < childList.length; i++) {
                    const item = childList[i];
                    if (item.index < firstIndex) {
                        item.index = firstIndex;
                    }
                    firstIndex = item.index + 1;
                }
            } else {
                let lastIndex = Math.max(sortList[sortList.length - 1].index, childLastIndex);
                for (let i = childList.length - 1; i >= 0; i--) {
                    const item = childList[i];
                    if (item.index > lastIndex) {
                        item.index = lastIndex;
                    }
                    lastIndex = item.index - 1;
                }
            }
            for (let i = 0; i < childList.length; i++) {
                const item = childList[i];
                newRenderIds[item.index] = item.id;
            }

            const oldRenderIds = [...renderIds];
            pullAll(oldRenderIds, ids);

            // console.log('diffIndex', {
            //     childList: [...childList],
            //     newRenderIds: [...newRenderIds],
            //     diffIndex,
            //     operateCount,
            //     oldRenderIds: [...oldRenderIds],
            //     ids: [...ids],
            // });

            for (let i = 0; i < renderIds.length; i++) {
                if (newRenderIds[i] === undefined) {
                    const id = oldRenderIds.shift();
                    if (id) {
                        newRenderIds[i] = id;
                    }
                }
            }
            renderIds.length = 0;
            renderIds.push(...newRenderIds);
        }

        this.framework.service.frame.emitSortLayer(data.frameId);

        if (pushHistory) {
            const newSortList = oldSortList.map((item) => {
                return {
                    ...item,
                    index: renderIds.indexOf(item.id),
                };
            });
            this.framework.service.history.write({
                type: HistoryType.SORT,
                data: {
                    originIndex: data.frameId,
                    oldSortList,
                    newSortList,
                },
                originIndex: data.frameId,
                needSyncRenderIds: true,
            });
        }
    }

    /**
     * 获取图层排序数据
     * @param layerId - 图层ID
     * @param ignoreTypes - 忽略的图层类型
     * @returns 排序数据
     */
    getSortData(layerId: string, ignoreTypes?: LayerType[]) {
        return this.framework.service.frame.getLayerSortData(layerId, ignoreTypes);
    }

    /**
     * 水平/垂直翻转
     * @param id - 图层 ID
     * @param isFlipH - 是否水平翻转
     * @param isFlipV - 是否垂直翻转
     * @returns
     */
    private async flip(id: string, isFlipH: boolean, isFlipV: boolean) {
        let oldLayer = this.framework.service.flatLayer.getLayer(id, true);
        if (!oldLayer) return;
        const disableFlip = this.framework.service.behavior.get(oldLayer)?.checkDisableFlip?.(oldLayer);
        if (disableFlip) return;
        const activePickLayer = this.framework.service.frame.getActivePickLayer();
        if (this.framework.service.flatLayer.checkIsGroup(oldLayer) && activePickLayer && activePickLayer.id !== id) {
            oldLayer = this.framework.service.flatLayer.getLayer(activePickLayer.id, true);
        }
        const layer = cloneDeep(oldLayer);

        const resetLayer = (layer: ILayerProps) => {
            const positions: IPoint[] = [];
            (layer.children || []).forEach((item: ILayerProps) => {
                const position = getRotatePosition(item);
                positions.push(...position);
            });
            if (!positions.length) return;
            resetGroupLayerRotate(layer, positions);
        };

        const flipLayer = async (layer: ILayerProps, isFlipH: boolean, isFlipV: boolean) => {
            const data = layer.data as IImageLayerProps;
            if (data?.imageTransform) {
                // 含内部变换模型的图层翻转内容
                const imageTransform = data.imageTransform as IImageTransform;
                if (isFlipH) {
                    imageTransform.flipH = !imageTransform.flipH;
                }
                if (isFlipV) {
                    imageTransform.flipV = !imageTransform.flipV;
                }
            } else {
                // 无内部变换模型的图层整体翻转
                if (isFlipH) {
                    layer.flipH = !layer.flipH;
                }
                if (isFlipV) {
                    layer.flipV = !layer.flipV;
                }
            }
            await this.framework.service.behavior.get(layer)?.onAfterFlip?.(layer);
        };

        if (this.framework.service.flatLayer.checkIsGroup(layer)) {
            // 图层组翻转需要对子图层进行重新计算
            const { left: groupLeft, top: groupTop, width: groupWidth, height: groupHeight } = layer;
            // 子组需要重置大小
            const groupLayers: ILayerProps[] = [];
            await asyncTraverseTrees([layer], async (layer: ILayerProps) => {
                const { left: layerLeft, top: layerTop, width: layerWidth, height: layerHeight } = layer;
                const width = layerWidth;
                const height = layerHeight;
                if (layer.children?.length > 0) {
                    groupLayers.push(layer);
                    return;
                }
                if (isFlipH) {
                    // 主图中心点和舞台的相对位置
                    const relativeStagePoSition = {
                        x: layerLeft + width / 2 - groupWidth / 2 - groupLeft,
                        y: layerTop + height / 2 - groupHeight / 2,
                    };
                    const flipXPosition = {
                        x: -relativeStagePoSition.x,
                        y: relativeStagePoSition.y,
                    };
                    const newPosition = {
                        x: flipXPosition.x + groupLeft + groupWidth / 2 - width / 2,
                        y: flipXPosition.y + groupHeight / 2 - height / 2,
                    };
                    layer.rotate = layer.rotate + (360 - layer.rotate * 2);
                    layer.left = newPosition.x;
                    layer.top = newPosition.y;
                }
                if (isFlipV) {
                    // 主图中心点和舞台的相对位置
                    const relativeStagePoSition = {
                        x: layerLeft + width / 2 - groupWidth / 2,
                        y: layerTop + height / 2 - groupHeight / 2 - groupTop,
                    };
                    const flipYPosition = {
                        x: relativeStagePoSition.x,
                        y: -relativeStagePoSition.y,
                    };
                    const newPosition = {
                        x: flipYPosition.x + groupWidth / 2 - width / 2,
                        y: flipYPosition.y + groupTop + groupHeight / 2 - height / 2,
                    };
                    layer.rotate = -layer.rotate;
                    layer.left = newPosition.x;
                    layer.top = newPosition.y;
                }
                await flipLayer(layer, isFlipH, isFlipV);
            });
            for (let i = groupLayers.length - 1; i > 0; i--) {
                const item = groupLayers[i];
                resetLayer(item);
            }
        } else {
            // 非图层组翻转
            await flipLayer(layer, isFlipH, isFlipV);
        }

        return {
            oldLayer,
            newLayer: layer,
        };
    }

    /**
     * 水平翻转
     *
     * @param layerId - 图层 ID
     */
    async flipX(layerId: string) {
        const res = await this.flip(layerId, true, false);
        if (!res) return;
        const { oldLayer, newLayer } = res;
        const trackFunc = getTrackFunc(this.identity, oldLayer, 'flipH');
        this.framework.service.frame.addOrUpdateLayer(newLayer);
        this.framework.service.history.write(oldLayer, newLayer, trackFunc);
        this.framework.service.frame.setActiveLayer(newLayer.id);
    }

    /**
     * 垂直翻转
     *
     * @param layerId - 图层 ID
     */
    async flipY(layerId: string) {
        const res = await this.flip(layerId, false, true);
        if (!res) return;
        const { oldLayer, newLayer } = res;
        const trackFunc = getTrackFunc(this.identity, oldLayer, 'flipV');
        this.framework.service.frame.addOrUpdateLayer(newLayer);
        this.framework.service.history.write(oldLayer, newLayer, trackFunc);
        this.framework.service.frame.setActiveLayer(newLayer.id);
    }

    /**
     * 真正隐藏图层，会保持到配方里，多端同步
     *
     * @param layerId - 图层 ID
     * @param isHidden - 是否隐藏
     * @param autoActive - 是否自动设置激活图层
     */
    setHidden(layerId: string, isHidden: boolean, autoActive = false) {
        const oldLayer = this.framework.service.flatLayer.getLayer(layerId, true);
        if (!oldLayer || oldLayer.hidden === isHidden) return;
        const newLayer = cloneDeep(oldLayer);
        traverseTree(newLayer, (item) => {
            this.framework.service.flatLayer.setHidden(item, isHidden);
            this.framework.service.frame.addOrUpdateLayer(item, undefined, true);
        });
        this.framework.service.edgelessCanvas.requestRenderAll();
        this.framework.service.history.write(oldLayer);
        if (isHidden) {
            this.framework.service.frame.delActiveLayer();
        } else {
            if (autoActive) {
                this.framework.service.frame.setActiveLayer(newLayer.id);
            }
        }
    }

    /**
     * 克隆图层
     *
     * @param layerId - 图层 ID
     */
    clone(layerId: string) {
        const oldLayer = this.framework.service.flatLayer.getLayer(layerId, true);
        if (!oldLayer) return;
        const disableClone = this.framework.service.behavior.get(oldLayer)?.checkDisableClone?.(oldLayer);
        if (disableClone) return;
        resetLayer(oldLayer);
        const isEdgelessMode = this.framework.service.edgelessCanvas.isEdgelessMode;
        if (isEdgelessMode) {
            oldLayer.left += COPY_DISTANCE;
            oldLayer.top += COPY_DISTANCE;
        }
        this.addLayer(
            {
                layer: oldLayer,
                checkFocus: true,
                isActiveNewLayer: true,
                isWriteHistory: true,
                trackFunc: getTrackFunc(this.identity, oldLayer, 'clone'),
            },
            isEdgelessMode ? undefined : 'diagonal',
            COPY_DISTANCE,
            isEdgelessMode ? layerId : this.preAddLayerId || layerId,
        );
    }

    /**
     * 获取图层
     * @param layerId - 图层 ID
     * @param withClone - 是否克隆
     * @returns 图层
     */

    getLayer(layerId: string, withClone = false) {
        return this.framework.service.flatLayer.getLayer(layerId, withClone);
    }

    /** 重置preAddLayerId */
    resetPreAddLayerId(id?: string) {
        this.preAddLayerId = id || '';
    }

    /**
     * 删除图层
     *
     * @param layerId - 图层 ID
     * @param prevLayer - 删除时的图层数据
     */
    delete(layerId: string, prevLayer?: ILayerProps) {
        const layer = prevLayer || this.framework.service.flatLayer.getLayer(layerId);
        if (!layer || this.framework.service.flatLayer.checkIsNeedLock(layerId)) return;
        if (!this.framework.service.flatLayer.checkPossibleDelete(layer)) return;
        const originLayer = cloneDeep(this.framework.service.flatLayer.getWrapLayer(layerId, true));
        // FIXME: 调整删除图层逻辑
        if (prevLayer && originLayer) {
            if (prevLayer.id === originLayer.id) {
                Object.assign(originLayer, prevLayer);
            } else if (originLayer.children) {
                traverseTrees(originLayer.children, (child: ILayerProps) => {
                    if (child.id === prevLayer.id) {
                        Object.assign(child, prevLayer);
                    }
                });
            }
        }
        const originIndex = this.framework.service.frame.getLayerFrameId(layerId) || 0;
        const historyData = this.framework.service.frame.delLayer(layerId);
        if (historyData) {
            if (prevLayer) {
                if (prevLayer.id === historyData.item.oldLayer.id) {
                    Object.assign(historyData.item.oldLayer, prevLayer);
                } else if (historyData.item.oldLayer.children) {
                    traverseTrees(historyData.item.oldLayer.children, (child: ILayerProps) => {
                        if (child.id === prevLayer.id) {
                            Object.assign(child, prevLayer);
                        }
                    });
                }
            }
            this.framework.service.history.write({
                type: HistoryType.DELETE,
                data: {
                    historyData,
                    originLayer,
                },
                originIndex,
                trackFunc: getTrackFunc(this.identity, layer, 'delete'),
                needSyncRenderIds: true,
            });
            const activeId = historyData.operate.activeId;
            if (activeId) {
                this.framework.service.frame.setActiveLayer(activeId);
            } else {
                this.framework.service.frame.delActiveLayer();
            }
        }
    }

    /**
     * 添加图层, 斜向偏移-向右下角偏移
     *
     * @param layerId - 图层 ID
     */
    addByDiagonal(layer: ILayerProps | IAddLayerArgs) {
        return this.addLayer(layer, 'diagonal', COPY_DISTANCE);
    }

    /**
     * 添加图层, 竖向偏移-向下方偏移
     *
     * @param layerId - 图层 ID
     */
    addByStraight(layer: ILayerProps | IAddLayerArgs) {
        return this.addLayer(layer, 'straight');
    }

    /**
     * 根据鼠标位置添加图层
     *
     * @param layer - 图层属性
     */
    addByMousePosition(layer: ILayerProps | IAddLayerArgs) {
        const mousePoint = getLastMousePoint();
        return this.addByDocPoint(layer, mousePoint);
    }

    /**
     * 根据文档坐标添加图层
     *
     * @param layer - 图层属性
     * @param docPoint - 文档坐标
     */
    addByDocPoint(layer: ILayerProps | IAddLayerArgs, docPoint: IPoint) {
        const { frame, transform } = this.framework.service;
        const { offsetTop } = frame.getFrameTransform(frame.getActiveFrameId());
        const scenePoint = transform.getScenePoint(docPoint);

        return this.addByStagePoint(layer, {
            ...scenePoint,
            y: scenePoint.y - offsetTop,
        });
    }

    /**
     * 根据显式位置添加图层
     *
     * @param layer - 图层属性
     * @param position - 固定舞台落点或左上角对角偏移基准
     * @returns 添加后的图层
     */
    addByInsertPosition(layer: ILayerProps | IAddLayerArgs, position: LayerInsertPosition) {
        if (position.type === 'stage-point') {
            return this.addByStagePoint(layer, position.point);
        }

        const currentLayer = isLayerProps(layer) ? layer : layer.layer;
        const gap = position.gap ?? COPY_DISTANCE;
        return this.addByStagePoint(layer, {
            x: position.layer.left + gap + currentLayer.width / 2,
            y: position.layer.top + gap + currentLayer.height / 2,
        });
    }

    /**
     * 根据舞台坐标添加图层
     *
     * @param layer - 图层属性
     * @param stagePoint - 舞台坐标
     */
    addByStagePoint(layer: ILayerProps | IAddLayerArgs, stagePoint: IPoint) {
        const currentLayer = isLayerProps(layer) ? layer : layer.layer;
        const { left, top } = currentLayer;

        currentLayer.left = toInteger(stagePoint.x - currentLayer.width / 2);
        currentLayer.top = toInteger(stagePoint.y - currentLayer.height / 2);

        if (currentLayer.children?.length) {
            const changeLeft = currentLayer.left - left;
            const changeTop = currentLayer.top - top;

            traverseTrees(currentLayer.children, (child: ILayerProps) => {
                child.left = toInteger(child.left + changeLeft);
                child.top = toInteger(child.top + changeTop);
            });
        }

        return this.addLayer(currentLayer);
    }

    /**
     * 添加图层
     * agent 模式会从新计算图层位置
     * @param layer - 图层属性
     */
    add(layerOrArgs: ILayerProps | IAddLayerArgs, agent?: IAgentInfo) {
        if (agent) {
            if (isLayerProps(layerOrArgs)) {
                layerOrArgs.extraInfo = {
                    ...layerOrArgs.extraInfo,
                    agent,
                };
            } else {
                layerOrArgs.layer.extraInfo = {
                    ...layerOrArgs.layer.extraInfo,
                    agent,
                };
            }
        }
        return this.addLayer(layerOrArgs);
    }

    /**
     * 添加图层
     *
     * @param layer - 图层属性
     * @param deviationMode - 偏移模式 diagonal: 斜向偏移 straight: 竖向偏移
     * @param offset - 斜向偏移的偏移量，竖向每次偏移上一个图层的高度
     */
    private addLayer(
        layer: ILayerProps | IAddLayerArgs,
        deviationMode?: 'diagonal' | 'straight',
        offset = 0,
        preAddLayerId = this.preAddLayerId,
    ) {
        if (deviationMode) {
            const currentLayer = isLayerProps(layer) ? layer : layer.layer;
            const newPosition = getAddLayerPosition(this.framework, currentLayer, offset, deviationMode, preAddLayerId);
            // console.log(
            //     'currentLayer',
            //     {
            //         left: currentLayer.left,
            //         top: currentLayer.top,
            //     },
            //     `preAddLayerId: ${preAddLayerId}`,
            // );
            if (currentLayer.children?.length) {
                const offsetX = newPosition.left - currentLayer.left;
                const offsetY = newPosition.top - currentLayer.top;
                resetLayer(currentLayer, offsetX, offsetY);
            } else {
                currentLayer.left = newPosition.left;
                currentLayer.top = newPosition.top;
                // console.log('newPosition', {
                //     left: currentLayer.left,
                //     top: currentLayer.top,
                // });
            }
        }
        const isLayer = isLayerProps(layer);
        let isActiveNewLayer = true;
        let isWriteHistory = true;
        let trackFunc = '';
        let checkFocus = false;
        let destOriginIndex: number | undefined = undefined;
        let extraInfo:
            | {
                  oldExtraInfo: IExtraInfo;
                  newExtraInfo: IExtraInfo;
              }
            | undefined;
        if (!isLayer) {
            const data = layer as unknown as IAddLayerArgs;
            isActiveNewLayer = data.isActiveNewLayer ?? true;
            isWriteHistory = data.isWriteHistory ?? true;
            layer = data.layer;
            trackFunc = data.trackFunc ?? getTrackFunc(this.identity, data.layer, 'add');
            extraInfo = data.extraInfo;
            checkFocus = data.checkFocus ?? false;
            destOriginIndex = data.destOriginIndex;
        } else {
            trackFunc = getTrackFunc(this.identity, layer as ILayerProps, 'add');
        }
        const resLayer = this.framework.service.frame.addOrUpdateLayer(layer as ILayerProps, destOriginIndex);
        if (extraInfo?.newExtraInfo) {
            this.framework.service.frame.setExtraInfoByLayerId(resLayer.id, extraInfo.newExtraInfo);
        }
        if (isWriteHistory) {
            const currentActiveInfo = this.framework.service.frame.getActiveInfo();
            this.framework.service.history.write({
                type: HistoryType.ADD,
                data: {
                    item: cloneDeep(resLayer),
                    extraInfo,
                    operate: {
                        oldActiveId: currentActiveInfo?.info.pickId || currentActiveInfo?.info.id,
                        isActiveNewLayer,
                    },
                },
                originIndex: resLayer.__originIndex,
                trackFunc,
                needSyncRenderIds: true,
            });
        }
        if (isActiveNewLayer) {
            this.framework.service.frame.setActiveLayer(resLayer.id);
            // console.log('checkFocus', { checkFocus });

            if (this.framework.service.edgelessCanvas.isEdgelessMode) {
                if (!checkFocus) {
                    this.framework.service.edgelessCanvas.setObjectToCenterByLayerId(resLayer.id);
                } else {
                    // 复制图层，不在可视区域，自动居中
                    const obj = this.framework.service.edgelessCanvas.getByLayerId(resLayer.id);
                    if (
                        obj &&
                        checkFocus &&
                        !obj.checkInViewport(this.framework.service.edgelessCanvas.blindAreas).allIn
                    ) {
                        this.framework.service.edgelessCanvas.setObjectToCenterByLayerId(resLayer.id);
                    }
                }
            }
        } else {
            this.framework.service.frame.delActiveLayer();
        }
        if (deviationMode) {
            this.preAddLayerId = resLayer.id;
        }

        return resLayer;
    }

    /**
     * 检查图层是否可以调整宽高
     * @param layer - 图层属性
     * @returns 是否可以调整宽高
     */
    checkCanResizeOperate(layer: ILayerProps) {
        return this.framework.service.flatLayer.checkCanResizeOperate(layer);
    }

    /**
     * 检查图层是否默认不等比拉伸
     * @param layerType - 图层类型
     * @returns 是否默认不等比拉伸
     */
    checkIsDefaultUnequalStretch(layer: ILayerProps) {
        return this.framework.service.behavior.get(layer)?.isDefaultUnequalStretch?.() ?? false;
    }

    /**
     * 遍历图层及其所有父级图层的属性是否为指定值
     *
     * @param layerId - 图层 ID
     * @param key - 属性键值, 可为 key1.key2.key3... 格式
     * @param value - 属性值
     * @returns Boolean
     */
    checkWrapLayerProps(layerId: string, key: string, value: unknown): boolean {
        const layer = this.framework.service.flatLayer.getLayer(layerId);

        if (!layer) return false;

        const keys = key.split('.');
        const getValue = (layer: ILayerProps): unknown => {
            return keys.reduce<unknown>((acc, key) => {
                if (acc == null || typeof acc !== 'object') return undefined;
                return (acc as Record<string, unknown>)[key];
            }, layer);
        };

        const checkLayerProps = (layer?: ILayerProps | null) => {
            if (!layer) return false;
            if (isEqual(getValue(layer), value)) {
                return true;
            }
            if (layer.parentId) {
                const parentLayer = this.framework.service.flatLayer.getLayer(layer.parentId);
                if (!parentLayer) return false;
                return checkLayerProps(parentLayer);
            }
            return false;
        };

        return checkLayerProps(layer);
    }
}

/**
 * 获取添加图层的坐标
 *
 * @param framework - 编辑器框架实例对象
 * @param layer - 图层属性
 * @param offset - 偏移量
 * @param deviationMode - 偏移模式 diagonal: 斜向偏移 straight: 竖向偏移
 * @param preLayerId - 之前的图层 ID
 */
function getAddLayerPosition(
    framework: IFramework,
    layer: ILayerProps,
    offset: number,
    deviationMode: 'diagonal' | 'straight',
    preLayerId?: string,
) {
    const stageSize = framework.service.frame.getActiveFrameSize();

    if (offset) {
        // 按照 1242px 的宽度计算偏移量
        offset = Math.ceil(stageSize.width / (1242 / offset));
    }

    const stageCenterPosition = {
        left: (stageSize.width - layer.width) / 2,
        top: (stageSize.height - layer.height) / 2,
    };
    if (!preLayerId) return stageCenterPosition;

    const preLayer = framework.service.flatLayer.getLayer(preLayerId);
    if (!preLayer) return stageCenterPosition;

    // 上一个图层不在当前激活画布内，返回中心位置
    const preLayerOriginIdex = framework.service.frame.getLayerFrameId(preLayerId);
    const activeOriginIndex = framework.service.frame.getActiveFrameId();
    if (preLayerOriginIdex !== activeOriginIndex) return stageCenterPosition;

    // 斜向偏移
    const newPosition = {
        left: preLayer.left + offset,
        top: preLayer.top + offset,
    };
    // 竖向偏移, 每次偏移一个图层的高度
    if (deviationMode === 'straight') {
        newPosition.left = stageCenterPosition.left;
        newPosition.top = preLayer.top + preLayer.height;
    }

    // 计算图层是否在舞台中
    const newLayerInfo = getLayerInfoWithRotate({
        ...newPosition,
        width: layer.width,
        height: layer.height,
        rotate: layer.rotate,
    });
    if (!isRectOutOfBounds(newLayerInfo, { ...stageSize, left: 0, top: 0 })) {
        return newPosition;
    }

    return stageCenterPosition;
}
